import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  BookOpen, 
  Video, 
  FileText, 
  ExternalLink,
  Star,
  Clock,
  TrendingUp,
  AlertCircle,
  Sparkles,
  RefreshCw,
  Target,
  Plus,
  GraduationCap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BibliotecaRecursos = ({ colaboradorId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [recomendacoesLMS, setRecomendacoesLMS] = useState(null);
  const [cursosRecomendados, setCursosRecomendados] = useState([]);
  const [novosCursosSugeridos, setNovosCursosSugeridos] = useState([]);

  useEffect(() => {
    if (colaboradorId) {
      carregarRecomendacoesLMS();
    }
  }, [colaboradorId]);

  const carregarRecomendacoesLMS = async () => {
    try {
      setLoading(true);
      
      // Tentar buscar recomendações existentes
      try {
        const response = await axios.get(
          `${API}/api/pdi-completo/biblioteca-recursos-lms-ia/${colaboradorId}`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }
        );
        setRecomendacoesLMS(response.data);
        setCursosRecomendados(response.data.cursos_lms_recomendados || []);
        setNovosCursosSugeridos(response.data.novos_cursos_sugeridos || []);
      } catch (error) {
        if (error.response?.status === 404) {
          // Não há recomendações ainda, gerar novas
          await gerarRecomendacoesLMS();
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Erro ao carregar recomendações:', error);
      toast.error('Erro ao carregar recursos do LMS. Verifique se você tem um diagnóstico PDI.');
    } finally {
      setLoading(false);
    }
  };

  const gerarRecomendacoesLMS = async () => {
    try {
      setLoading(true);
      toast.info('Analisando cursos do LMS com IA...', { duration: 3000 });
      
      const response = await axios.post(
        `${API}/api/pdi-completo/biblioteca-recursos-lms-ia?colaborador_id=${colaboradorId}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setRecomendacoesLMS(response.data);
      setCursosRecomendados(response.data.cursos_lms_recomendados || []);
      setNovosCursosSugeridos(response.data.novos_cursos_sugeridos || []);
      
      const totalRec = response.data.total_recomendados || 0;
      const totalSug = response.data.total_sugestoes_criacao || 0;
      
      if (totalRec > 0 || totalSug > 0) {
        toast.success(`${totalRec} cursos recomendados e ${totalSug} sugestões de criação!`);
      } else {
        toast.info('Nenhum curso encontrado no LMS. Veja as sugestões de criação.');
      }
    } catch (error) {
      console.error('Erro ao gerar recomendações:', error);
      if (error.response?.status === 404) {
        toast.error('Execute o Diagnóstico IA primeiro para gerar recomendações.');
      } else {
        toast.error('Erro ao gerar recomendações.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getNivelColor = (nivel) => {
    const colors = {
      'beginner': 'bg-green-100 text-green-800',
      'intermediate': 'bg-yellow-100 text-yellow-800',
      'advanced': 'bg-red-100 text-red-800'
    };
    return colors[nivel] || colors['beginner'];
  };

  const getNivelLabel = (nivel) => {
    const labels = {
      'beginner': 'Iniciante',
      'intermediate': 'Intermediário',
      'advanced': 'Avançado'
    };
    return labels[nivel] || nivel;
  };

  const getRelevanciaColor = (relevancia) => {
    const colors = {
      'alta': 'bg-green-100 text-green-800',
      'media': 'bg-yellow-100 text-yellow-800',
      'baixa': 'bg-gray-100 text-gray-800'
    };
    return colors[relevancia] || colors['media'];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Biblioteca de Recursos - Integração LMS</h3>
                <p className="text-sm text-gray-600 font-normal">
                  Cursos recomendados do LMS com base no seu PDI
                </p>
              </div>
            </div>
            <Button 
              onClick={gerarRecomendacoesLMS} 
              disabled={loading}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Analisando...' : 'Atualizar'}
            </Button>
          </CardTitle>
        </CardHeader>
        
        {recomendacoesLMS && (
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-600">
                  {recomendacoesLMS.total_cursos_lms || 0}
                </div>
                <div className="text-sm text-gray-600">Cursos no LMS</div>
              </div>
              <div className="bg-white rounded-lg p-3">
                <div className="text-2xl font-bold text-green-600">
                  {recomendacoesLMS.total_recomendados || 0}
                </div>
                <div className="text-sm text-gray-600">Recomendados</div>
              </div>
              <div className="bg-white rounded-lg p-3">
                <div className="text-2xl font-bold text-orange-600">
                  {recomendacoesLMS.total_sugestoes_criacao || 0}
                </div>
                <div className="text-sm text-gray-600">Sugestões para Criar</div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Cursos LMS Recomendados */}
      {cursosRecomendados.length > 0 && (
        <Card className="border-2 border-green-200">
          <CardHeader className="bg-green-50">
            <CardTitle className="flex items-center space-x-2">
              <Star className="h-5 w-5 text-green-600" />
              <span>Cursos Recomendados do LMS ({cursosRecomendados.length})</span>
            </CardTitle>
            <p className="text-sm text-gray-600">Cursos disponíveis que atendem suas necessidades</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {cursosRecomendados.map((curso, idx) => (
                <Card key={curso.id || idx} className="border hover:border-green-500 transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <BookOpen className="h-5 w-5 text-blue-600" />
                          <h4 className="font-bold text-lg">{curso.title}</h4>
                        </div>
                        
                        <p className="text-gray-600 mb-3">{curso.description}</p>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge className={getNivelColor(curso.level)}>
                            {getNivelLabel(curso.level)}
                          </Badge>
                          {curso.category && (
                            <Badge variant="outline">{curso.category}</Badge>
                          )}
                          {curso.recomendacao_ia?.relevancia && (
                            <Badge className={getRelevanciaColor(curso.recomendacao_ia.relevancia)}>
                              Relevância: {curso.recomendacao_ia.relevancia}
                            </Badge>
                          )}
                          {curso.recomendacao_ia?.prioridade_uso && (
                            <Badge className="bg-purple-100 text-purple-800">
                              <Target className="h-3 w-3 mr-1" />
                              Prioridade {curso.recomendacao_ia.prioridade_uso}
                            </Badge>
                          )}
                        </div>
                        
                        {curso.recomendacao_ia?.competencia_relacionada && (
                          <div className="bg-blue-50 rounded-lg p-3 mb-3">
                            <div className="flex items-start space-x-2">
                              <Target className="h-4 w-4 text-blue-600 mt-0.5" />
                              <div>
                                <div className="text-sm font-semibold text-blue-900">
                                  Competência: {curso.recomendacao_ia.competencia_relacionada}
                                </div>
                                {curso.recomendacao_ia?.justificativa && (
                                  <div className="text-sm text-gray-700 mt-1">
                                    {curso.recomendacao_ia.justificativa}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {curso.duration_hours}h
                          </div>
                          {curso.tags && curso.tags.length > 0 && (
                            <div className="flex items-center gap-1">
                              {curso.tags.slice(0, 3).map((tag, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <Button 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            // Navegar para LMS com o curso selecionado via state
                            navigate('/lms', { 
                              state: { 
                                selectedCourseId: curso.id,
                                openCourse: true 
                              } 
                            });
                          }}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Acessar Curso
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Novos Cursos Sugeridos */}
      {novosCursosSugeridos.length > 0 && (
        <Card className="border-2 border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center space-x-2">
              <Sparkles className="h-5 w-5 text-orange-600" />
              <span>Sugestões de Novos Cursos ({novosCursosSugeridos.length})</span>
            </CardTitle>
            <p className="text-sm text-gray-600">
              A IA sugere criar estes cursos no LMS para atender melhor suas necessidades
            </p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {novosCursosSugeridos.map((sugestao, idx) => (
                <Card key={idx} className="border-2 border-dashed border-orange-300 bg-orange-50/30">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Plus className="h-5 w-5 text-orange-600" />
                          <h4 className="font-bold text-lg">{sugestao.titulo_sugerido}</h4>
                        </div>
                        
                        <p className="text-gray-600 mb-3">{sugestao.descricao_sugerida}</p>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {sugestao.nivel_sugerido && (
                            <Badge className={getNivelColor(sugestao.nivel_sugerido)}>
                              {getNivelLabel(sugestao.nivel_sugerido)}
                            </Badge>
                          )}
                          {sugestao.categoria_sugerida && (
                            <Badge variant="outline">{sugestao.categoria_sugerida}</Badge>
                          )}
                          {sugestao.prioridade_criacao && (
                            <Badge className="bg-orange-100 text-orange-800">
                              Prioridade: {sugestao.prioridade_criacao}
                            </Badge>
                          )}
                        </div>
                        
                        {sugestao.competencia_relacionada && (
                          <div className="bg-white rounded-lg p-3 mb-3 border border-orange-200">
                            <div className="flex items-start space-x-2">
                              <Target className="h-4 w-4 text-orange-600 mt-0.5" />
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  Competência: {sugestao.competencia_relacionada}
                                </div>
                                {sugestao.justificativa && (
                                  <div className="text-sm text-gray-700 mt-1">
                                    {sugestao.justificativa}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {sugestao.objetivos_aprendizagem && sugestao.objetivos_aprendizagem.length > 0 && (
                          <div className="bg-white rounded-lg p-3 mb-3 border border-orange-200">
                            <div className="text-sm font-semibold text-gray-900 mb-2">
                              Objetivos de Aprendizagem:
                            </div>
                            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                              {sugestao.objetivos_aprendizagem.map((obj, i) => (
                                <li key={i}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          {sugestao.duracao_estimada_horas && (
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              ~{sugestao.duracao_estimada_horas}h (estimado)
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <Button 
                          variant="outline"
                          className="border-orange-600 text-orange-600 hover:bg-orange-50"
                          onClick={() => {
                            toast.info('Funcionalidade de criação rápida em desenvolvimento');
                            // Futuramente: abrir modal para criar curso no LMS
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Criar no LMS
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Observações da IA */}
      {recomendacoesLMS?.observacoes_gerais && (
        <Card className="border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center space-x-2 text-base">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <span>Observações da IA</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-gray-700">{recomendacoesLMS.observacoes_gerais}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !recomendacoesLMS && (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="p-12 text-center">
            <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Biblioteca de Recursos Integrada ao LMS
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Clique em "Atualizar" para que a IA analise os cursos disponíveis no LMS e 
              recomende os mais adequados ao seu PDI
            </p>
            <Button 
              onClick={gerarRecomendacoesLMS}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Gerar Recomendações com IA
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BibliotecaRecursos;
